import React, { useEffect, useState } from 'react';
import { Alert, Button, Drawer, Form, Input, Select, Space, message } from 'antd';
import { putVisitNC } from '../../services/api';
import { getApiError } from '../../utils/apiError';
import { Question } from './checklistTypes';

const { TextArea } = Input;

const LEVELS = [
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'MAJOR', label: 'Major' },
  { value: 'MINOR', label: 'Minor' },
  { value: 'CONCERN', label: 'Concern' },
];

interface Props {
  visitId: string;
  question: Question | null;
  categories: { id: string; name: string }[];
  contractors: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}

const NCDrawer: React.FC<Props> = ({ visitId, question, categories, contractors, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!question) return;
    setError('');
    form.resetFields();
    if (question.nc) {
      form.setFieldsValue({
        level: question.nc.level,
        category_id: question.nc.category_id,
        description: question.nc.description,
        rectification_required: question.nc.rectification_required,
        contractor_id: question.nc.contractor_id || undefined,
        location: question.nc.location || '',
      });
    }
  }, [question, form]);

  const save = async () => {
    if (!question) return;
    const values = await form.validateFields();
    setSaving(true);
    setError('');
    try {
      await putVisitNC(visitId, question.id, { ...values, contractor_id: values.contractor_id || null });
      message.success('NC saved.');
      onSaved();
    } catch (err) {
      setError(getApiError(err, 'Could not save the NC.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={question ? `NC for ${question.code}` : ''}
      open={!!question}
      onClose={onClose}
      size={480}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>Close</Button>
          <Button type="primary" onClick={save} loading={saving}>Save NC</Button>
        </Space>
      }
    >
      {question && <Alert type="info" showIcon message={question.text} style={{ marginBottom: 16 }} />}
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical">
        <Form.Item name="level" label="NC level" rules={[{ required: true, message: 'Choose the level' }]}>
          <Select options={LEVELS} />
        </Form.Item>
        <Form.Item name="category_id" label="Category" rules={[{ required: true, message: 'Choose the category' }]}>
          <Select showSearch optionFilterProp="label" options={categories.map((c) => ({ value: c.id, label: c.name }))} />
        </Form.Item>
        <Form.Item
          name="location" label="Location"
          rules={[{ required: true, message: 'Say where it is (tower, floor, grid, member...)' }]}
          extra="For example: Tower B, 7th floor, grid C-4, column C4"
        >
          <Input maxLength={300} />
        </Form.Item>
        <Form.Item name="description" label="What was found" rules={[{ required: true, message: 'Describe the finding' }]}>
          <TextArea rows={4} />
        </Form.Item>
        <Form.Item
          name="rectification_required" label="Rectification required"
          rules={[{ required: true, message: 'State what must be done' }]}
        >
          <TextArea rows={3} />
        </Form.Item>
        <Form.Item name="contractor_id" label="Contractor (optional)">
          <Select
            allowClear showSearch optionFilterProp="label"
            options={contractors.map((c) => ({ value: c.id, label: c.name }))}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default NCDrawer;
